/**
 * Humanizes a raw numeric value by appending a metric prefix (kilo, mega, etc).
 * Currently only does not support centi, deci, deca, hecto.
 *
 * @example
 * humanizeNumericValue(1000, "Wh") // "1kWh"
 * humanizeNumericValue(0.003) // "3m"
 *
 * @param value Raw value
 * @param measurementUnit This is appended to the end as is
 * @param opts Options
 */
export function humanizeNumericValue(
    value: number,
    measurementUnit = "",
    opts?: {
        /**
         * The number of decimal places to round to. Defaults to 20. Valid values are 0-20.
         */
        precision?: number;
        /**
         * The lower limit of order of magnitude in which to convert. For upper limit use `maxMagnitude`.
         *
         * Each number represents the next order of magnitude in which a conversion occurs (kilo, tera, milli, micro, etc). A `minMagnitude` of 0 will convert from `Infinity` to 1, and a `minMagnitude` of -1 will convert from `Infinity` to 0.001 (1 milliunit).
         *
         * @example
         * humanizeNumericValue(1000, "Wh", undefined, { minMagnitude: 0 }) // "1kWh"
         * humanizeNumericValue(0.001, "Wh", undefined, { minMagnitude: 0 }) // "0.001Wh"
         * humanizeNumericValue(0.001, "Wh", undefined, { minMagnitude: -1 }) // "1mWh"
         */
        minMagnitude?: number;
        /**
         * The upper limit of order of magnitude in which to convert. For lower limit use `minMagnitude`.
         *
         * Each number represents the next order of magnitude in which a conversion occurs (kilo, tera, milli, micro, etc). A `maxMagnitude` of 0 will convert from `-Infinity` to 999, and a `maxMagnitude` of 1 will convert from `-Infinity` to 9999 (9.999 kilounit).
         *
         * @example
         * humanizeNumericValue(0.001, "Wh", undefined, { maxMagnitude: 0 }) // "1mWh"
         * humanizeNumericValue(0.001, "Wh", undefined, { maxMagnitude: -1 }) // "0.001Wh"
         * humanizeNumericValue(1000, "Wh", undefined, { maxMagnitude: 0 }) // "1000Wh"
         * humanizeNumericValue(0.001, "Wh", undefined, { minMagnitude: -1 }) // "1mWh"
         */
        maxMagnitude?: number;
        /**
         * Whether to display separators within numbers (1,000,000). Defaults to `false`.
         */
        displaySeparator?: boolean;
    }
): string {
    if (value === undefined) {
        return "";
    }
    const {
        precision = 20,
        minMagnitude,
        maxMagnitude,
        displaySeparator = false,
    } = opts ?? {};
    const absValue = Math.abs(value);
    const calculatedOrderOfMagnitude =
        absValue === 0 || !Number.isFinite(absValue)
            ? 0
            : Math.floor(Math.log10(absValue) / 3) * 3;
    const orderOfMagnitude = Math.min(
        Math.max(
            calculatedOrderOfMagnitude,
            minMagnitude !== undefined ? minMagnitude * 3 : -30
        ),
        maxMagnitude !== undefined ? maxMagnitude * 3 : 30
    );

    const positiveLetterArray = " kMGTPEZYRQ".split("");
    const negativeLetterArray = " mμnpfazyrq".split("");
    // Zero has no metric prefix, even when a bound excludes magnitude zero.
    const exponent = absValue === 0 ? 0 : orderOfMagnitude;
    const magnitudeLetter =
        (exponent >= 0 ? positiveLetterArray : negativeLetterArray)[
            Math.abs(exponent) / 3
        ] ?? "";
    const formattedValue = value / Math.pow(10, exponent);
    const res =
        formattedValue.toLocaleString("en-US", {
            maximumFractionDigits: Math.max(precision, 0), // in case precision is negative
            useGrouping: displaySeparator,
        }) +
        magnitudeLetter.trim() +
        measurementUnit.trim();
    return res;
}
